import { baseGatewayApiV1 } from '@verantos-dev/app-state-base';
import { COHORTS_PATH } from '@verantos-dev/core-constants';
import {
  type Cohort,
  type CohortVersion,
  type CohortCRUDResponse,
  type DeleteCohortData,
  type CreateCohortWithDatasetData,
  type RenameCohortData,
} from '@verantos-dev/types-cohorts';
import { type ListResponse, type ObjectResponse } from '@verantos-dev/types-common';

import { COHORTS_BASE_PATH } from './constants';

export const cohortApiSlice = baseGatewayApiV1
  .enhanceEndpoints({
    addTagTypes: ['cohorts', 'cohortAttrition', 'cohortSummary', 'subjects', 'projects'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      /** Cohort Profile Endpoints */

      getCohorts: builder.query<
        ListResponse<Cohort>,
        { projectId: string; urlParams: string | null }
      >({
        query: ({ projectId, urlParams }) => {
          const params = urlParams ? `${encodeURI(urlParams)}` : '';
          return `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}${params}`;
        },
        providesTags: ['cohorts', { type: 'cohorts', id: 'LIST' }],
      }),

      createCohort: builder.mutation<
        ObjectResponse<Cohort>,
        { projectId: string; data: CreateCohortWithDatasetData }
      >({
        query: ({ projectId, data }) => ({
          url: `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}`,
          method: 'POST',
          body: { ...data },
        }),
        invalidatesTags: ['cohorts', 'projects'],
      }),

      deleteCohort: builder.mutation<CohortCRUDResponse, DeleteCohortData>({
        query: ({ projectId, cohortId }) => ({
          url: `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, { cohortId }) => [
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ],
      }),
      renameCohort: builder.mutation<CohortCRUDResponse, RenameCohortData>({
        query: ({ cohortId, projectId, data }) => ({
          url: `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}/rename`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (result, error, { cohortId }) => [
          { type: 'cohorts', id: cohortId },
          'cohorts',
          'projects',
        ],
      }),

      /** Cohort Version Endpoints */
      getCohortVersion: builder.query<
        ObjectResponse<CohortVersion>,
        { projectId: string; cohortId: string; cohortVersionId: string }
      >({
        query: ({ projectId, cohortId, cohortVersionId }) =>
          `/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}/${cohortId}/cohort-version/${cohortVersionId}`,
        providesTags: (result) => [{ type: 'cohorts', id: result?.data?.cohortVersionId }],
      }),
    }),
  });
export const {
  /** Cohort Profile Endpoints */
  useGetCohortsQuery,
  useCreateCohortMutation,
  useDeleteCohortMutation,
  useRenameCohortMutation,

  /** Cohort Version Endpoints */
  useGetCohortVersionQuery,
} = cohortApiSlice;
